import type { ReactElement } from "react";
import { useEffect, useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import type { IFilterViewBaseProps } from "app/pages/sales-intelligence/modules/advanced-search-filtering/types/filterInstance";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import {
    isProfileEmpty,
    isUnsavedProfile,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/common";
import { contactsSearchProfilesQueryKeys } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/queries/queryKeyFactory";
import type { Profile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/common";
import type { ProfilesResponseDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

import type EmployeePersonasFilterModel from "../../../models/EmployeePersonasFilter/EmployeePersonasFilterModel";

import EmployeeProfilesFilter from "./EmployeeProfilesFilter";
import { StyledEmployeeProfilesWrapper } from "./styles";
import { UpdateEventName } from "./types";

const EmployeeProfilesFilterContainer = ({
    value = [],
    filter,
    onFilterUpdate,
}: IFilterViewBaseProps<EmployeePersonasFilterModel>): ReactElement | null => {
    const queryClient = useQueryClient();
    const { seniority, departments } = filter.options;

    // Champion is treated like Admin for shared authoring, same convention as the Personas page.
    const canManageShared = salesSettingsHelper.isHighLevelAccount();

    const [localValue, setLocalValue] = useState<readonly Profile[]>(value);

    useEffect(() => {
        if (!localValue.length && value.length) {
            setLocalValue(value);
            return;
        }

        setLocalValue(
            localValue.map((localValueItem) => {
                const valueItem: Profile | undefined = value.find((item) => item.id === localValueItem.id);

                if (!valueItem) {
                    return localValueItem;
                }

                return { ...localValueItem, isEnabled: !!valueItem.isEnabled };
            }),
        );
    }, [value]);

    const handleUpdateLocalState = (profiles: readonly Profile[], eventName: UpdateEventName) => {
        setLocalValue(profiles);

        queryClient.setQueryData(
            contactsSearchProfilesQueryKeys.allProfiles(),
            eventName === UpdateEventName.Remove ||
                eventName === UpdateEventName.Rename ||
                eventName === UpdateEventName.Saved
                ? {
                      profiles,
                      totalCount: profiles.length,
                  }
                : (oldData: ProfilesResponseDto) => {
                      // data mutation
                      oldData.profiles = profiles;
                      oldData.totalCount = profiles.length;

                      // return old reference to avoid LeadsSearchFiltersContainer component rerender on change data trigger
                      return oldData;
                  },
        );
    };

    const handleUpdateFilterAndLocalState = (profiles: readonly Profile[], eventName: UpdateEventName) => {
        handleUpdateLocalState(profiles, eventName);
        onFilterUpdate(profiles);
    };

    const isAddProfileButtonDisabled = useMemo(() => {
        return localValue.some(isUnsavedProfile) || localValue.every(isProfileEmpty);
    }, [localValue]);

    return (
        <StyledEmployeeProfilesWrapper>
            <EmployeeProfilesFilter
                value={localValue}
                seniority={seniority}
                departments={departments}
                isAddProfileButtonDisabled={isAddProfileButtonDisabled}
                canManageShared={canManageShared}
                onUpdateLocalState={handleUpdateLocalState}
                onUpdateFilterAndLocalState={handleUpdateFilterAndLocalState}
            />
        </StyledEmployeeProfilesWrapper>
    );
};

export default EmployeeProfilesFilterContainer;
